<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import type { Subscription } from "@modules/espace-entreprise/types/subscription"
import {
    formatSubscriptions,
    getLegacySubscriptions,
} from "@modules/espace-entreprise/helpers/subscription.helper"
import { useCompanyPlafond } from "@core/composables/useCompanyPlafond"
import {
    filtresDepuisUrl,
    filtresLiberent,
    ROUTE_SOURCES_FACTUREES,
    type FiltresActifs,
} from "@modules/espace-entreprise/components/company/billing/billingSourceCriteres"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            hors_taxe: "* Tous les prix sont indiqués hors taxes",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Liste de tous les abonnements
     */
    subscriptions: Subscription[]
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// STATE
// ================================
/** Routeur, absent hors application (stories) : les liens sont alors inertes */
const router = useRouter()

/**
 * Décompte servi (DEV-621) : nombre facturé et bloc `plafond`, lus une fois
 * pour la carte.
 */
const {
    plafond,
    total: totalServi,
    isLoaded: isDecompteLoaded,
    fetch: fetchPlafond,
} = useCompanyPlafond()

/**
 * Nombre de sources facturées servi : `plafond.facturees`, sinon `total`.
 * `null` sans nombre servi, en erreur ou avant la réponse : aucun nombre
 * n'est écrit (une absence n'est jamais un 0).
 */
const getSourcesFacturees = computed((): number | null =>
    isDecompteLoaded.value
        ? (plafond.value?.facturees ?? totalServi.value ?? null)
        : null
)

// ================================
// METHODS
// ================================

/** « Voir les sources » : la page des sources, sans filtre */
function onClickSource() {
    void ouvrirListe({})
}

/**
 * « Libérer de l'espace » (DEV-2480) : la page des sources filtrée sur
 * celles qui libèrent une place, comme le lien des courriels d'alerte
 * (`?si_supprimee=libere`) ; le retour arrière rend la carte
 */
function onClickLiberer() {
    void ouvrirListe(filtresLiberent())
}

/**
 * Un nombre du tableau de synthèse : la page des sources s'ouvre sur les
 * mêmes sources (mêmes filtres de critères, demande de Mathieu du 15/10)
 * @param filtres - filtres de la ligne
 */
function onClickFiltres(filtres: FiltresActifs) {
    void ouvrirListe(filtresDepuisUrl(filtres))
}

/**
 * Mène à la page des sources (demande du 16/10 : une page, plus un modal),
 * les filtres dans la query
 * @param filtres - filtres du bandeau, `{}` pour la liste entière
 */
async function ouvrirListe(filtres: FiltresActifs) {
    if (!router) return
    try {
        await router.push({
            name: ROUTE_SOURCES_FACTUREES,
            query: { ...filtres },
        })
    } catch {
        // Navigation refusée : la carte reste affichée
    }
}

// ================================
// COMPUTED
// ================================

/**
 * Récupère les abonnements formatés (Energy Management / Energy Analytics avec leurs modules)
 */
const getSubscriptions = computed(() => {
    return formatSubscriptions(props.subscriptions)
})

/**
 * Récupère les abonnements legacy (anciens abonnements)
 */
const getLegacySubscriptionsList = computed(() => {
    return getLegacySubscriptions(props.subscriptions)
})

/**
 * Combine les abonnements formatés et legacy pour l'affichage
 */
const getAllSubscriptions = computed(() => {
    return [...getSubscriptions.value, ...getLegacySubscriptionsList.value]
})

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    void fetchPlafond()
})
</script>

<template>
    <div class="company-subscriptions-list w-full">
        <!-- ================================ -->
        <!-- LISTE DES ABONNEMENTS -->
        <!-- ================================ -->
        <div class="flex flex-col gap-0">
            <CompanySubscriptionsListItem
                v-for="(subscription, index) in getAllSubscriptions"
                :key="`subscription-${index}`"
                :subscription="subscription"
                :sources="getSourcesFacturees"
                :plafond="plafond"
                :decompte-loaded="isDecompteLoaded"
                @click:source="onClickSource"
                @click:liberer="onClickLiberer"
                @click:filtres="onClickFiltres"
            />
        </div>

        <!-- ================================ -->
        <!-- MESSAGE HORS TAXE -->
        <!-- ================================ -->
        <p
            v-if="subscriptions.length > 0"
            class="company-subscriptions-list__star text-sm text-surface-600 mt-4"
        >
            {{ t("hors_taxe") }}
        </p>
    </div>
</template>
